import { useState } from 'react'
import { Box } from '@mui/material'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import SupportMentorsListing from 'src/supportPortal/components/common/mentor/listing/SupportMentorsListing'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { styled } from '@mui/material/styles'

const TabsContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(216, 101, 0, 0.08) 0%, rgba(216, 101, 0, 0.12) 100%)',
  padding: theme.spacing(2),
  borderRadius: '0 0 16px 16px',
  borderBottom: '1px solid rgba(216, 101, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: '0 0 12px 12px',
  },
}))

/**
 * Therapists listing surface.
 *
 * Tabs:
 *   - "All"        → every therapist on the platform.
 *   - "Clinical"   → therapists currently attached to at least one clinic brand. The label
 *                    is a misnomer for "clinic-attached" (it does NOT mean clinical-
 *                    psychology); kept for backwards compatibility with the existing
 *                    backend split (/therapists/list vs /therapists/clinical/list).
 *
 * Phase 14x — added discipline-aware tabs ("Psychotherapy", "Nutrition Consultation") that
 * slice either the All or Clinical bucket client-side based on
 * TeacherUnEditableFields.approvedTherapyDisciplines (already on the response). The Calendar
 * action button per row is removed here — it surfaced a per-practitioner calendar route the
 * support team isn't using on this page.
 */
export default function MentorBookings() {
  const [selectedScope, setSelectedScope] = useState('clinical')
  const [selectedDiscipline, setSelectedDiscipline] = useState('any')

  const scopeTabs = [
    { id: 'all', label: 'All' },
    { id: 'clinical', label: 'Clinical' },
  ]

  const disciplineTabs = [
    { id: 'any', label: 'Any discipline' },
    { id: 'psychotherapy', label: 'Psychotherapy' },
    { id: 'nutrition-consultation', label: 'Nutrition Consultation' },
  ]

  const mentorsListApiSignature =
    selectedScope === 'all'
      ? '/api/secure/cc/support/therapists/list'
      : '/api/secure/cc/support/therapists/clinical/list'

  return (
    <>
      <SupportHeader heading="Therapists" showBackButton />
      <TabsContainer>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <BaseUITabsComponent
            variant="contained"
            tabs={scopeTabs}
            value={selectedScope}
            unselectedColor="rgba(71, 85, 105, 0.85)"
            selectedColor="rgba(216, 101, 0, 0.95)"
            hoverItemColor="rgba(216, 101, 0, 0.9)"
            onChange={setSelectedScope}
            getTabId={(tab) => tab.id}
            getTabLabel={(tab) => tab.label}
            incicatorColor="primary"
          />
          {/* Phase 14x — discipline filter sits beneath the scope tabs so the agent first
              picks the bucket (All / Clinical) and then narrows by discipline. Both sets of
              tabs are independent; "Any discipline" is the no-op default. */}
          <Box sx={{ mt: 1 }}>
            <BaseUITabsComponent
              variant="contained"
              tabs={disciplineTabs}
              value={selectedDiscipline}
              unselectedColor="rgba(71, 85, 105, 0.7)"
              selectedColor="rgba(124, 58, 237, 0.95)"
              hoverItemColor="rgba(124, 58, 237, 0.9)"
              onChange={setSelectedDiscipline}
              getTabId={(tab) => tab.id}
              getTabLabel={(tab) => tab.label}
              incicatorColor="secondary"
            />
          </Box>
        </Box>
      </TabsContainer>
      <SupportMentorsListing
        mentorsListApiSignature={mentorsListApiSignature}
        listApiParams={{}}
        variant="therapist"
        heading="Therapists"
        // Phase 14x — Calendar per-row action removed; the support team doesn't use the
        // per-practitioner calendar route on this page (the in-context appointments view
        // covers the same ground via the Appointments / Roster buttons).
        exculudeMenuItemsList={['calendar']}
        hideHeading
        showOnboardingButton
        onboardingPractitionerType="therapist"
        disciplineFilter={selectedDiscipline === 'any' ? null : selectedDiscipline}
        showDisciplineChips
      />
    </>
  )
}
